import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Sparkles, Mic, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { markAsVisited, getGuestId } from '../services/analytics';
import { saveUserProfile } from '../utils/userPreferences';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          markAsVisited();
          navigate('/');
        }
      } catch (error) {
        console.log('Not authenticated');
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // Hide Emergent badge on this page
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.display = 'none';
    }
    return () => {
      if (badge) {
        badge.style.display = 'flex';
      }
    };
  }, []);

  const handleLoginChange = (field, value) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!loginData.email || !loginData.password) {
      setErrorMessage('Please enter email and password');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginData.email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoggingIn(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password,
          device_id: getGuestId()  // Send device ID
        })
      });

      // Check status before trying to read body
      if (response.ok) {
        try {
          const data = await response.json();
          markAsVisited();
          
          if (data.user) {
            saveUserProfile({
              name: data.user.name || '',
              email: data.user.email || '',
              mobile: data.user.mobile || '',
              age: data.user.age || '',
              picture: data.user.picture || ''
            });
            localStorage.setItem('smartreadgo_auth_token', 'authenticated');
          }

          navigate('/', { replace: true });
        } catch (jsonError) {
          console.error('Success response parse error:', jsonError);
          // If we can't parse success response, still navigate
          navigate('/', { replace: true });
        }
      } else {
        // For error responses, try to get the error message
        try {
          const data = await response.json();
          setErrorMessage(data.detail || 'Invalid email or password');
        } catch (jsonError) {
          // If we can't parse the error, show a generic message based on status
          if (response.status === 401) {
            setErrorMessage('Invalid email or password');
          } else {
            setErrorMessage('Login failed. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage('Connection error. Please check your internet and try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGuestContinue = () => {
    markAsVisited();
    getGuestId();
    localStorage.setItem('smartreadgo_guest_mode', 'true');
    navigate('/');
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Safe area padding for mobile status bar */}
      <div className="pt-[env(safe-area-inset-top,0px)]" />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {/* Logo */}
        <div className="mb-6">
          <img
            src="/images/logo.png"
            alt="SmartReadGo Logo"
            className="w-32 h-32 object-contain"
          />
        </div>

        {/* App Title */}
        <h1 className="text-3xl font-bold mb-2">
          <span className="text-gray-900">Smart</span>
          <span className="text-orange-500">ReadGo</span>
        </h1>

        {/* Tagline */}
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
          Your Personal Audio News Assistant
        </h2>
        <p className="text-gray-600 text-center mb-8">
          Built for safe, hands-free news consumption.
        </p>

        {/* Login Buttons / Login Form */}
        <div className="w-full max-w-sm space-y-4">
          {!showLoginForm ? (
            <>
              {/* Login with Email Button */}
              <Button
                onClick={() => setShowLoginForm(true)}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full"
              >
                Login with Email
              </Button>

              {/* Sign Up Link */}
              <div className="text-center py-2">
                <span className="text-gray-600">Don't have an account yet? </span>
                <button
                  onClick={() => navigate('/signup')}
                  className="text-blue-700 font-semibold hover:underline"
                >
                  Sign Up
                </button>
              </div>

              {/* Guest Button */}
              <Button
                onClick={handleGuestContinue}
                variant="outline"
                className="w-full border-gray-300 text-gray-700 py-6 text-lg font-semibold rounded-full flex items-center justify-center gap-3 hover:bg-gray-100"
              >
                <User className="w-5 h-5" />
                Continue as Guest
              </Button>
            </>
          ) : (
            <>
              {/* Email Login Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-900 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={loginData.email}
                    onChange={(e) => handleLoginChange('email', e.target.value)}
                    className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-900 font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => handleLoginChange('password', e.target.value)}
                      className="text-base py-6 pr-12 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {/* Forgot Password Link */}
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-blue-700 text-sm hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {/* Error Message Display */}
                {errorMessage && (
                  <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full"
                >
                  {isLoggingIn ? 'Logging in...' : 'Login'}
                </Button>
              </form>

              {/* Back to options */}
              <button
                onClick={() => setShowLoginForm(false)}
                className="w-full text-center text-gray-600 hover:text-gray-800 py-2"
              >
                ← Back to login options
              </button>

              {/* Sign Up Link */}
              <div className="text-center py-2">
                <span className="text-gray-600">Don't have an account yet? </span>
                <button
                  onClick={() => navigate('/signup')}
                  className="text-blue-700 font-semibold hover:underline"
                >
                  Sign Up
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="px-6 pb-8">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-gray-700">
            <Sparkles className="w-5 h-5 text-blue-700" />
            <span className="font-medium">AI-powered summaries</span>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Mic className="w-5 h-5 text-blue-700" />
            <span className="font-medium">Voice-controlled navigation</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
